import { getReferenceId } from '@quromedical/fhir-common';
import type {
  AdmissionGetAllResponse,
  AdmissionGetResponseWithVitals,
  VitalsLiveDataPayload,
} from '@quromedical/models';
import { mergeObjects } from 'helpers';
import { getNextTokenFromLink } from 'hooks/usePaginatedData';
import { useCallback, useMemo } from 'react';
import { strings } from 'strings';
import { trpc } from 'trpc';

const getNextToken = (data: AdmissionGetAllResponse): string | undefined =>
  getNextTokenFromLink(data.link);

/**
 * Will get the active assignments as well as the vitals shadow for the patient
 */

export const useActiveAssignedAdmissions = (
  options: UseActiveAssignedAdmissionsOptions,
) => {
  const { count = 1000, locationId } = options;

  const key = `active-assigned-admissions-${locationId}`;

  const {
    error: trpcError,
    isLoading: isLoadingActiveAdmissions,
    data,
    isRefetching,
    refetch,
  } = trpc.admission.getAll.useQuery(
    {
      pageToken: options?.nextToken,
      count,
      status: 'in-progress',
      includeVitals: true,
      location: locationId,
    },
    { refetchOnWindowFocus: false, refetchOnMount: false },
  );

  const onSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const isLoading = useMemo(() => {
    return isLoadingActiveAdmissions || isRefetching;
  }, [isLoadingActiveAdmissions, isRefetching]);

  const activeAdmissionsWithVitals = useMemo(
    () => (data?.admissions as AdmissionGetResponseWithVitals[]) || [],
    [data],
  );

  const error = trpcError ? strings.GenericErrorMessage : undefined;

  return {
    data: activeAdmissionsWithVitals,
    isLoading,
    error,
    key,
    getNextToken,
    invalidate: onSuccess,
  };
};

const mapAdmissionToVitalsPartial = ({
  vitals,
  subject,
}: AdmissionGetResponseWithVitals): VitalsLiveDataPayload => {
  const patientId = getReferenceId(subject);
  const hasVitals = patientId && vitals;

  if (!hasVitals) {
    return {};
  }

  return {
    [patientId]: vitals,
  };
};

const getVitalsFromAdmissions = (
  admissions: AdmissionGetResponseWithVitals[] = [],
): VitalsLiveDataPayload => {
  const vitalPartials = admissions.map<VitalsLiveDataPayload>(
    mapAdmissionToVitalsPartial,
  );

  return mergeObjects(vitalPartials, {});
};

/**
 * Use the latest vitals shadow for all active assigned patient
 */
export const useActiveAssignedAdmissionVitals = (
  options: UseActiveAssignedAdmissionsOptions = {},
) => {
  const { data } = useActiveAssignedAdmissions(options);

  return getVitalsFromAdmissions(data);
};

interface UseActiveAssignedAdmissionsOptions {
  count?: number;
  locationId?: string;
  nextToken?: string;
}
